/**
 * Correlated normal columns by the Cholesky method.
 *
 * To draw a row x with mean μ, standard deviations σ and correlation matrix R: factor
 * R = L Lᵀ with L lower-triangular (Cholesky), draw z of independent standard normals and
 * set x = μ + σ ∘ (L z). The covariance of x is then diag(σ) R diag(σ), as wanted.
 *
 * With `exact` on, the independent columns are first centred, made exactly uncorrelated
 * by Gram–Schmidt and scaled to unit sample variance (n − 1), so the sample means, SDs
 * and correlation matrix equal the targets to rounding error — the idea behind R's
 * `MASS::mvrnorm(..., empirical = TRUE)`.
 */

/** Lower-triangular L with L Lᵀ = matrix. Throws if the matrix is not positive definite. */
export function cholesky(matrix: readonly (readonly number[])[]): number[][] {
  const size = matrix.length;
  const lower = Array.from({ length: size }, () =>
    new Array<number>(size).fill(0)
  );
  for (let i = 0; i < size; i++) {
    for (let j = 0; j <= i; j++) {
      let total = matrix[i][j];
      for (let k = 0; k < j; k++) {
        total -= lower[i][k] * lower[j][k];
      }
      if (i === j) {
        if (total <= 0) {
          throw new RangeError(
            "The correlation matrix is not positive definite"
          );
        }
        lower[i][j] = Math.sqrt(total);
      } else {
        lower[i][j] = total / lower[j][j];
      }
    }
  }
  return lower;
}

function columnMean(column: readonly number[]): number {
  let total = 0;
  for (const value of column) {
    total += value;
  }
  return total / column.length;
}

/** Centre a column and scale it to unit sample SD (n − 1). */
export function standardize(column: readonly number[]): number[] {
  const centre = columnMean(column);
  let squares = 0;
  for (const value of column) {
    squares += (value - centre) ** 2;
  }
  const sd = Math.sqrt(squares / (column.length - 1));
  return column.map((value) => (value - centre) / (sd > 0 ? sd : 1));
}

/**
 * Columns with sample mean 0, sample SD 1 and exactly zero sample correlation with each
 * other: each column loses its projection on the ones before it, then is rescaled.
 */
export function orthonormalColumns(
  columns: readonly (readonly number[])[]
): number[][] {
  const done: number[][] = [];
  for (const column of columns) {
    let residual = standardize(column);
    for (const earlier of done) {
      let dot = 0;
      let norm = 0;
      for (let i = 0; i < residual.length; i++) {
        dot += residual[i] * earlier[i];
        norm += earlier[i] * earlier[i];
      }
      const slope = dot / norm;
      residual = residual.map((value, i) => value - slope * earlier[i]);
    }
    done.push(standardize(residual));
  }
  return done;
}

/**
 * `n` rows of correlated normal variables, returned as one array per variable.
 * `normal` supplies independent standard normal draws (seeded by the caller).
 */
export function correlatedNormals(
  n: number,
  means: readonly number[],
  sds: readonly number[],
  correlation: readonly (readonly number[])[],
  normal: () => number,
  exact = false
): number[][] {
  const size = means.length;
  const lower = cholesky(correlation);
  const independent = Array.from({ length: size }, () =>
    Array.from({ length: n }, () => normal())
  );
  const z = exact ? orthonormalColumns(independent) : independent;
  return means.map((mean, v) =>
    Array.from({ length: n }, (_, row) => {
      let combined = 0;
      for (let k = 0; k <= v; k++) {
        combined += lower[v][k] * z[k][row];
      }
      return mean + sds[v] * combined;
    })
  );
}
